// calculatorStore.js — Zustand store holding all canonical inputs in SI units.
// Persisted to localStorage so the user's setup and saved profiles survive reloads.
import { create } from "zustand";
import { persist } from "zustand/middleware";

const defaultInputs = {
  // Projectile (canonical SI)
  massKg: 0.001030, // 15.89 gr
  v0: 240, // m/s
  bc: 0.03, // G1
  selectedPelletId: "jsb-jumbo-1589",
  // Optics & zero
  zeroDistance: 30, // m
  sightHeight: 0.04, // m (4 cm)
  targetDistance: 50, // m
  inclination: 0, // deg
  // Wind & atmosphere
  windSpeed: 3, // m/s
  windDir: 90, // deg (from-clock: 0 front, 90 right, 180 back, 270 left)
  tempC: 20,
  altitude: 100, // m
  pressureHpa: null, // measured surface pressure (hPa); null => derive from altitude
  vitalWindow: 0.03, // m (MPBR hit-window diameter, default 3 cm)
};

export const useCalculatorStore = create(
  persist(
    (set, get) => ({
      ...defaultInputs,

      // Display preferences
      language: "fi", // 'fi' | 'en'
      location: "", // user-entered or GPS-derived, empty by default
      unitSystem: "metric", // 'metric' | 'imperial'
      holdoverUnit: "cm", // 'cm' | 'inch' | 'mrad' | 'moa'
      clickMrad: 0.1,
      clickId: "mrad-0.1",

      savedProfiles: [],

      setField: (key, value) => set({ [key]: value }),

      setLanguage: (language) => set({ language }),
      setLocation: (location) => set({ location }),
      setUnitSystem: (system) => set({ unitSystem: system }),
      setHoldoverUnit: (unit) => set({ holdoverUnit: unit }),
      setClick: (id, mrad) => set({ clickId: id, clickMrad: mrad }),

      selectPellet: (pellet) =>
        set({
          selectedPelletId: pellet.id,
          massKg: pellet.weight_g / 1000,
          bc: pellet.bc,
          v0: pellet.v0_ms,
        }),

      resetInputs: () => set({ ...defaultInputs }),

      saveProfile: (name) => {
        const s = get();
        const profile = {
          id: `${Date.now()}`,
          name,
          data: {
            massKg: s.massKg,
            v0: s.v0,
            bc: s.bc,
            selectedPelletId: s.selectedPelletId,
            zeroDistance: s.zeroDistance,
            sightHeight: s.sightHeight,
            targetDistance: s.targetDistance,
            inclination: s.inclination,
            windSpeed: s.windSpeed,
            windDir: s.windDir,
            tempC: s.tempC,
            altitude: s.altitude,
            pressureHpa: s.pressureHpa,
            vitalWindow: s.vitalWindow,
          },
        };
        set({ savedProfiles: [...s.savedProfiles, profile] });
        return profile;
      },

      loadProfile: (id) => {
        const p = get().savedProfiles.find((x) => x.id === id);
        if (p) set({ ...p.data });
      },

      deleteProfile: (id) =>
        set({ savedProfiles: get().savedProfiles.filter((x) => x.id !== id) }),
    }),
    { name: "dase-ballistics-store" }
  )
);
